import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { usePageHeader } from '@utils/hooks/use-page-header';
import Tabs, { Tab } from '@components/atoms/ui-kit/tabs.tsx';
import { Package, BarChart3, Key, Users, FileText, Shield } from 'lucide-react';
import Input from '@components/atoms/ui-kit/input.tsx';
import Button from '@components/atoms/ui-kit/button.tsx';
import { SaveIcon } from '@components/shared/icons.tsx';
import cn from 'classnames';

import { useUpdateResellerCustomer } from '@utils/api/resellers/use-update-customer-reseller.tsx';
import { useGetResellerCustomer } from '@utils/api/resellers/use-get-cutomer-reseller.tsx';

import { SubscriptionDetails } from '@components/my-account/reseller-customer/subscription-details.tsx';
import { UsersDetails } from '@components/my-account/reseller-customer/users-details';
import { UsageDetails } from '@components/my-account/reseller-customer/usage-details.tsx';
import { TranscriptionsDetails } from '@components/my-account/reseller-customer/transcriptions-details.tsx';
import TokenDetails from '@components/my-account/reseller-customer/token-details.tsx';
import { LicenseRequestsDetails } from '@components/my-account/reseller-customer/license-requests-details.tsx';
import { AxiosError } from 'axios';
import { BaseError } from '@utils/types/shared/base.ts';
import toast from 'react-hot-toast';
import { buildResellerBreadcrumbTrail } from '@utils/breadcrump-trails/build-reseller-breadcrump-trail.ts';
import {
  BreadcrumbItem,
  Breadcrumbs
} from '@components/atoms/ui-kit/breadcrumbs.tsx';
import { PATHS } from '../../router/paths.ts';
import PhoneField from '@components/atoms/ui-kit/phone-input.tsx';
import Checkbox from '@components/atoms/ui-kit/checkbox.tsx';
import { Text } from '@components/atoms/ui-kit/text.tsx';

type Params = { resellerId: string };

export default function ResellerCustomer() {
  const { t } = useTranslation();
  const { resellerId } = useParams<Params>();
  const [resellerName, setResellerName] = useState('');
  const [resellerEmail, setResellerEmail] = useState('');
  const [resellerPhone, setResellerPhone] = useState('');
  const [resellerStatus, setResellerStatus] = useState(false);
  const [originalData, setOriginalData] = useState({
    name: '',
    email: '',
    phone: '',
    is_active: false
  });
  const [isUpdating, setIsUpdating] = useState(false);
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([]);

  const { data: resellerData } = useGetResellerCustomer(resellerId || '');

  const updateResellerCustomer = useUpdateResellerCustomer();

  useEffect(() => {
    const d = resellerData?.result;
    if (d) {
      const { name = '', email = '', phone = '', is_active = false } = d;
      setResellerName(name);
      setResellerEmail(email);
      setResellerPhone(phone);
      setResellerStatus(is_active);
      setOriginalData({ name, email, phone, is_active });
    }
  }, [resellerData]);

  const hasChanges =
    resellerName !== originalData.name ||
    resellerEmail !== originalData.email ||
    resellerPhone !== originalData.phone ||
    resellerStatus !== originalData.is_active;

  const saveChanges = () => {
    if (!resellerId) return;

    setIsUpdating(true);

    updateResellerCustomer.mutate(
      {
        customerId: resellerId,
        data: {
          name: resellerName,
          email: resellerEmail,
          phone: resellerPhone,
          is_active: resellerStatus
        }
      },
      {
        onSuccess: () => {
          toast.success(t('reseller-customer-updated-successfully'));
          setOriginalData({
            name: resellerName,
            email: resellerEmail,
            phone: resellerPhone,
            is_active: resellerStatus
          });
        },
        onError: (error: AxiosError<BaseError>) => {
          const data = error?.response?.data;
          let msg = t('failed-to-update-reseller-customer');
          if (Array.isArray(data?.detail)) {
            msg = data.detail
              .map(
                (e: { loc?: string[]; msg: string }) =>
                  `${e.loc?.[1] ?? 'field'}: ${e.msg}`
              )
              .join('\n');
          } else if (data?.message) msg = data.message;
          else if (data?.error_description) msg = data.error_description;
          toast.error(msg);
        },
        onSettled: () => setIsUpdating(false)
      }
    );
  };

  const tabName = (Icon: typeof Package, key: string) => (
    <span className="flex items-center gap-2">
      <Icon className="h-4 w-4" />
      {t(key)}
    </span>
  );

  const resellerTabs: Tab[] = [
    {
      id: 0,
      name: tabName(Package, 'subscription'),
      component: <SubscriptionDetails resellerId={resellerId} />
    },
    {
      id: 1,
      name: tabName(BarChart3, 'usage'),
      component: <UsageDetails resellerId={resellerId} />
    },
    {
      id: 2,
      name: tabName(Key, 'tokens'),
      component: <TokenDetails resellerId={resellerId} />
    },
    {
      id: 3,
      name: tabName(Users, 'users'),
      component: <UsersDetails resellerId={resellerId} />
    },
    {
      id: 4,
      name: tabName(FileText, 'transcriptions'),
      component: <TranscriptionsDetails resellerId={resellerId} />
    },
    {
      id: 5,
      name: tabName(Shield, 'licensing'),
      component: <LicenseRequestsDetails resellerId={resellerId} />
    }
  ];

  useEffect(() => {
    if (resellerId) {
      buildResellerBreadcrumbTrail(resellerId).then(setBreadcrumbs);
    }
  }, [resellerId]);

  usePageHeader({
    title: resellerData?.result ? (
      <Breadcrumbs
        items={breadcrumbs.slice(0, -1)}
        currentFolderName={resellerData.result.name}
        rootLabel="Resellers"
        rootPath={PATHS().RESELLER}
      />
    ) : (
      t('reseller-customer')
    ),
    description: t(
      'manage-reseller-customer-details-subscriptions-usage-tokens-users-and-transcriptions'
    )
  });

  return (
    <div className="bg-app min-h-screen">
      <div className="space-y-3 px-4 pb-4 sm:pb-0">
        {/* Organization Details Card - Compact */}
        <div className="rounded-2xl px-4 py-3 shadow-card bg-app-soft border border-hairline">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 flex-1">
              <Input
                id="organization"
                label={t('organization-name')}
                value={resellerName}
                onChange={e => setResellerName(e.target.value)}
                placeholder={t('name')}
                size="small"
              />
              <Input
                id="email"
                label={t('email')}
                value={resellerEmail}
                onChange={e => setResellerEmail(e.target.value)}
                placeholder={t('email')}
                size="small"
              />
              <PhoneField
                id="phone"
                label={t('phone')}
                value={resellerPhone}
                onChange={e164 => setResellerPhone(e164)}
                defaultCountry="NL"
                required={false}
                placeholder="612345678"
                size="small"
                className={cn('block w-full')}
                messages={{
                  required: t('field-required'),
                  invalid: t('enter-a-valid-international-phone-number')
                }}
              />
              <div>
                <Text
                  as="label"
                  size="sm"
                  weight="semibold"
                  className="block mb-2"
                >
                  {t('status')}
                </Text>
                <div className="h-9 px-3 rounded-2xl border border-hairline bg-app-soft flex items-center gap-3">
                  <Checkbox
                    id="status"
                    name="status"
                    label={t('active')}
                    checked={resellerStatus}
                    onChange={e => setResellerStatus(e.target.checked)}
                    variant="primary"
                    wrapperClass="!m-0"
                    labelClass="text-sm font-medium"
                  />
                  <Text
                    as="span"
                    size="xs"
                    tone="muted"
                    className="hidden lg:inline"
                  >
                    {t('user-status')}
                  </Text>
                </div>
              </div>
            </div>
            <Button
              onClick={saveChanges}
              icon={<SaveIcon width={14} />}
              size="small"
              label={isUpdating ? t('updating') : t('save-changes')}
              variant="secondary"
              disabled={isUpdating || !hasChanges}
              className="hidden sm:flex self-end"
            />
          </div>
        </div>

        {/* Tabs Card */}
        <div className="pt-4">
          <Tabs
            tabs={resellerTabs}
            searchParams
            minHeight="55vh"
            maxHeight="70vh"
          />
        </div>
      </div>
    </div>
  );
}
